import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  taxSarsServiceSchema,
  taxFaqSchema,
  generateBreadcrumbSchema,
} from "@/lib/structuredData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  FileText,
  Shield,
  Calculator,
  Receipt,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const taxServices = [
  {
    icon: FileText,
    title: "Annual Income Tax Returns (ITR14)",
    description:
      "We prepare and submit your company's annual income tax return (ITR14) to SARS, ensuring all allowable deductions are claimed and your taxable income is correctly calculated.",
  },
  {
    icon: Calculator,
    title: "Provisional Tax Returns (IRP6)",
    description:
      "Two provisional tax submissions per year to spread your tax liability. We calculate accurate estimates based on your financial performance to avoid underpayment penalties.",
  },
  {
    icon: Receipt,
    title: "VAT201 Returns",
    description:
      "For VAT-registered businesses, we prepare and submit VAT201 returns bi-monthly or monthly. We ensure input VAT is correctly claimed and output VAT is properly declared.",
  },
  {
    icon: Shield,
    title: "SARS Audit Support",
    description:
      "If SARS selects your business for audit, we provide full support — preparation of documentation, response to queries, and professional representation during proceedings.",
  },
];

const taxTimeline = [
  { item: "Provisional Tax (1st Period)", deadline: "Within 6 months of year start", description: "First estimate of annual taxable income" },
  { item: "Provisional Tax (2nd Period)", deadline: "At financial year-end", description: "Revised estimate based on actual performance" },
  { item: "Annual Tax Return (ITR14)", deadline: "Within 12 months of year-end", description: "Final return with audited/reviewed financials" },
  { item: "VAT201", deadline: "25th of month following VAT period", description: "Input and output VAT declaration" },
];

const TaxReturnsSarsCompliance = () => {
  return (
    <Layout>
      <SEO
        title="Tax Returns & SARS Compliance | South Africa | SAICA Registered"
        description="Professional tax return preparation and SARS compliance by a SAICA registered CA. Annual income tax, provisional tax, VAT201, and audit support. Never miss a SARS deadline again."
        canonical="/tax-returns-sars-compliance"
        structuredData={[
          taxSarsServiceSchema,
          taxFaqSchema,
          generateBreadcrumbSchema([
            { name: "Home", url: "https://bookkeepingservices.co.za/" },
            { name: "Services", url: "https://bookkeepingservices.co.za/services" },
            { name: "Tax Returns & SARS Compliance", url: "https://bookkeepingservices.co.za/tax-returns-sars-compliance" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/30" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-medium text-primary mb-4 tracking-wide uppercase animate-fade-up">
              Tax & Compliance
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-up">
              Tax Returns & SARS Compliance
              <span className="block text-primary">Handled for You</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-up max-w-2xl mx-auto mb-8">
              Tax compliance in South Africa means submitting accurate returns to SARS on time — 
              including annual income tax (ITR14), provisional tax (IRP6), and VAT201. 
              We manage every submission so you stay penalty-free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up">
              <Link to="/book-a-call">
                <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/packages">
                <Button size="lg" variant="outline" className="btn-outline-primary gap-2 text-base px-8 h-14">
                  View Packages
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="What We Handle"
            title="Complete Tax & SARS Compliance"
            description="Every tax submission managed by a SAICA registered Chartered Accountant."
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {taxServices.map((service) => (
              <div key={service.title} className="card-elevated p-8">
                <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center text-primary mb-5">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Calendar */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Tax Calendar"
            title="Key SARS Deadlines"
            description="Know your deadlines. We track and submit everything on time."
          />
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-xl border border-border overflow-hidden shadow-soft">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-accent">
                      <th className="text-left p-4 font-heading font-semibold text-foreground">Submission</th>
                      <th className="text-left p-4 font-heading font-semibold text-foreground">Deadline</th>
                      <th className="text-left p-4 font-heading font-semibold text-foreground">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxTimeline.map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-4 font-medium text-foreground">{row.item}</td>
                        <td className="p-4 text-muted-foreground">{row.deadline}</td>
                        <td className="p-4 text-muted-foreground">{row.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-6 p-4 rounded-xl bg-destructive/5 border border-destructive/20 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">
                <strong>SARS penalties</strong> for late submissions range from R250 to R16,000 per month, plus interest on outstanding amounts. We ensure you never pay a penalty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* E-E-A-T Signals */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Why Us"
            title="SAICA Registered Tax Professionals"
            description="Trust, expertise, and a track record of SARS compliance."
          />
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              "SAICA registered Chartered Accountant — the highest accounting qualification in South Africa",
              "Years of experience with SARS submissions, audits, and dispute resolution",
              "Proactive deadline management — we notify you before any submission is due",
              "Tax-efficient structuring advice to minimise your legal tax liability",
              "Full SARS audit support and professional representation",
              "Compliant with POPIA — your financial data is handled with strict confidentiality",
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="FAQ"
            title="Tax & SARS Compliance Questions"
            description="Clear answers to common tax questions for South African businesses."
          />
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {taxFaqSchema.mainEntity.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-xl px-6 hover:border-primary/20 transition-colors duration-300 data-[state=open]:border-primary/30 data-[state=open]:shadow-md"
                >
                  <AccordionTrigger className="text-left font-heading text-base font-semibold hover:no-underline hover:text-primary transition-colors py-5">
                    {faq.name}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {faq.acceptedAnswer.text}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
};

export default TaxReturnsSarsCompliance;
